import React, { useState, useEffect, useCallback } from 'react';
import api, { fetchAllPages } from '@/lib/api';
import SearchSelect from '@/components/common/SearchSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Search, Upload, Download, Eye, Filter, FileText, Calendar, User, Building } from 'lucide-react';
import { toast } from 'sonner';

const OWNER_TYPES = ['PARTICIPANT', 'STAFF', 'HR', 'INCIDENT', 'HOUSE', 'SYSTEM'];
const CATEGORIES = ['ID', 'SERVICE_AGREEMENT', 'CONSENT', 'POLICY', 'HR', 'INCIDENT_DOC',
                    'NDIS_PLAN', 'BEHAVIOUR_SUPPORT_PLAN', 'MEDICAL', 'GUARDIAN_DOCUMENT', 'RISK_ASSESSMENT_DOC',
                    'OTHER'];

export default function DocumentLibraryPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterOwnerType, setFilterOwnerType] = useState('');
  const [filterExpiring, setFilterExpiring] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showUpload, setShowUpload] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [stats, setStats] = useState(null);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, pageSize: 20 });
      if (search) params.append('search', search);
      if (filterCategory) params.append('category', filterCategory);
      if (filterOwnerType) params.append('ownerType', filterOwnerType);
      if (filterExpiring === 'soon') {
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + 30);
        params.append('expiryBefore', futureDate.toISOString());
      }
      const res = await api.get(`/documents?${params}`);
      setDocuments(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [page, search, filterCategory, filterOwnerType, filterExpiring]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/documents/stats/summary');
      setStats(res.data.data);
    } catch (err) {}
  };

  useEffect(() => {
    fetchDocuments();
    fetchStats();
  }, [fetchDocuments]);

  const handleDownload = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.fileName || 'document';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download document');
    }
  };

  return (
    <div className="space-y-6" data-testid="document-library-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Document Library</h1>
          <p className="text-muted-foreground">Manage all organization documents</p>
        </div>
        <Dialog open={showUpload} onOpenChange={setShowUpload}>
          <DialogTrigger asChild>
            <Button data-testid="upload-document-btn"><Upload className="h-4 w-4 mr-2" />Upload Document</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
            <UploadDocumentForm onSuccess={() => { setShowUpload(false); fetchDocuments(); fetchStats(); }} />
          </DialogContent>
        </Dialog>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Total" value={stats.total} icon={FileText} />
          <StatCard label="Active" value={stats.active} icon={FileText} color="text-green-600" />
          <StatCard label="Archived" value={stats.archived} icon={FileText} color="text-gray-500" />
          <StatCard label="Expiring Soon" value={stats.expiringSoon} icon={Calendar} color="text-orange-600" />
          <StatCard label="Review Due" value={stats.reviewDue} icon={Calendar} color="text-red-600" />
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={filterCategory} onValueChange={(v) => { setFilterCategory(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[160px]" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterOwnerType} onValueChange={(v) => { setFilterOwnerType(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[150px]" data-testid="filter-owner-type"><SelectValue placeholder="Owner Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {OWNER_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterExpiring} onValueChange={(v) => { setFilterExpiring(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[150px]" data-testid="filter-expiry"><SelectValue placeholder="Expiry" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="soon">Expiring Soon</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Expiry Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : documents.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No documents found</TableCell></TableRow>
              ) : documents.map(doc => (
                <TableRow key={doc.id} className="hover:bg-muted/50" data-testid={`doc-row-${doc.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{doc.title}</span>
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge status={doc.category} /></TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <span className="text-muted-foreground">{doc.ownerType}</span>
                      {doc.ownerName && <span className="ml-1">- {doc.ownerName}</span>}
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge status={doc.status} /></TableCell>
                  <TableCell>
                    {doc.expiryDate ? (
                      <span className={isExpiringSoon(doc.expiryDate) ? 'text-orange-600 font-medium' : ''}>
                        {doc.expiryDate.slice(0, 10)}
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setPreviewDoc(doc)} data-testid={`preview-${doc.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDownload(doc)} data-testid={`download-${doc.id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {total > 20 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">Page {page} of {Math.ceil(total / 20)}</span>
          <Button variant="outline" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Document Details</DialogTitle></DialogHeader>
          {previewDoc && <DocumentPreview doc={previewDoc} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color = 'text-primary' }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-muted ${color}`}><Icon className="h-5 w-5" /></div>
        <div>
          <p className="text-2xl font-bold">{value || 0}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function UploadDocumentForm({ onSuccess }) {
  const [form, setForm] = useState({ ownerType: '', ownerId: '', category: '', title: '', expiryDate: '', visibility: 'INTERNAL' });
  const [file, setFile] = useState(null);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (form.ownerType === 'PARTICIPANT') {
      fetchAllPages('/participants').then(items => setOwners((items || []).map(p => ({ id: p.id, name: `${p.first_name} ${p.last_name}` })))).catch(() => setOwners([]));   // NEW-007: all records, not the first 100
    } else if (form.ownerType === 'STAFF') {
      fetchAllPages('/staff').then(items => setOwners((items || []).map(s => ({ id: s.id, name: s.user_full_name || s.full_name || 'Staff' })))).catch(() => setOwners([]));   // NEW-007: all records, not the first 100
    } else {
      setOwners([]);
    }
  }, [form.ownerType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !form.ownerType || !form.ownerId || !form.category || !form.title) {
      toast.error('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('ownerType', form.ownerType);
      formData.append('ownerId', form.ownerId);
      formData.append('category', form.category);
      formData.append('title', form.title);
      formData.append('visibility', form.visibility);
      if (form.expiryDate) formData.append('expiryDate', form.expiryDate);
      await api.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded successfully');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Owner Type *</Label>
          <Select value={form.ownerType} onValueChange={(v) => setForm(f => ({ ...f, ownerType: v, ownerId: '' }))}>
            <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>
              {OWNER_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Owner *</Label>
          <SearchSelect
            options={owners.map(o => ({ value: o.id, label: o.name }))}
            value={form.ownerId}
            onChange={(v) => setForm(f => ({ ...f, ownerId: v }))}
            disabled={!form.ownerType || owners.length === 0}
            placeholder="Select owner"
            searchPlaceholder="Search owners…"
            testId="upload-owner-select"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Category *</Label>
          <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v }))}>
            <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Visibility</Label>
          <Select value={form.visibility} onValueChange={(v) => setForm(f => ({ ...f, visibility: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="INTERNAL">Internal Only</SelectItem>
              <SelectItem value="PORTAL_VISIBLE">Portal Visible</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Title *</Label>
        <Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Document title" />
      </div>
      <div>
        <Label>Expiry Date (optional)</Label>
        <TrinitiDatePicker value={form.expiryDate} onChange={(v) => setForm(f => ({ ...f, expiryDate: v }))} testId="doc-library-expiry" />
      </div>
      <div>
        <Label>File *</Label>
        <Input type="file" onChange={(e) => setFile(e.target.files[0])} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" />
      </div>
      <Button type="submit" disabled={loading} className="w-full">{loading ? 'Uploading...' : 'Upload Document'}</Button>
    </form>
  );
}

function DocumentPreview({ doc }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><Label className="text-muted-foreground">Title</Label><p className="font-medium">{doc.title}</p></div>
        <div><Label className="text-muted-foreground">Category</Label><p><StatusBadge status={doc.category} /></p></div>
        <div><Label className="text-muted-foreground">Owner Type</Label><p>{doc.ownerType}</p></div>
        <div><Label className="text-muted-foreground">Status</Label><p><StatusBadge status={doc.status} /></p></div>
        <div><Label className="text-muted-foreground">Visibility</Label><p><StatusBadge status={doc.visibility} /></p></div>
        <div><Label className="text-muted-foreground">Version</Label><p>{doc.version || 1}</p></div>
        <div><Label className="text-muted-foreground">File Name</Label><p className="truncate">{doc.fileName || '-'}</p></div>
        <div><Label className="text-muted-foreground">File Size</Label><p>{doc.fileSize ? `${(doc.fileSize / 1024).toFixed(1)} KB` : '-'}</p></div>
        <div><Label className="text-muted-foreground">Expiry Date</Label><p>{doc.expiryDate?.slice(0, 10) || '-'}</p></div>
        <div><Label className="text-muted-foreground">Review Date</Label><p>{doc.reviewDate?.slice(0, 10) || '-'}</p></div>
        <div><Label className="text-muted-foreground">Created</Label><p>{doc.createdAt?.slice(0, 10) || '-'}</p></div>
        <div><Label className="text-muted-foreground">Updated</Label><p>{doc.updatedAt?.slice(0, 10) || '-'}</p></div>
      </div>
    </div>
  );
}

function isExpiringSoon(dateStr) {
  if (!dateStr) return false;
  const expiry = new Date(dateStr);
  const now = new Date();
  const diff = (expiry - now) / (1000 * 60 * 60 * 24);
  return diff <= 30 && diff >= 0;
}
